import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GoldButton } from '@/components/common/GoldButton';
import { useAuth } from '@/context/AuthContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { t } from '@/i18n';
import {
  canonicalizeEmailForSubmit,
  isSupportedEmail,
} from '@/utils/authValidation';
import styles from './VerifyEmailPage.module.css';

type Phase = 'form' | 'submitting' | 'legacy-verifying' | 'success';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, resendVerification } = useAuth();
  const onApiError = useApiErrorToast();
  const legacyToken = searchParams.get('token') ?? '';
  const codeJustSent = searchParams.get('sent') === '1';

  const [phase, setPhase] = useState<Phase>(
    legacyToken ? 'legacy-verifying' : 'form',
  );
  const email = searchParams.get('email') ?? '';
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(codeJustSent);

  useEffect(() => {
    if (!codeJustSent) return;
    const id = setTimeout(() => setResendCooldown(false), 60_000);
    return () => clearTimeout(id);
  }, [codeJustSent]);

  useEffect(() => {
    if (!legacyToken) return;

    let cancelled = false;
    verifyEmail({ token: legacyToken })
      .then(() => {
        if (!cancelled) setPhase('success');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        onApiError(error, t.auth.verify.invalidCode);
        setPhase('form');
      });

    return () => {
      cancelled = true;
    };
  }, [legacyToken, onApiError, verifyEmail]);

  useEffect(() => {
    if (phase !== 'success') return;
    const id = setTimeout(() => navigate('/', { replace: true }), 1200);
    return () => clearTimeout(id);
  }, [navigate, phase]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (
      !isSupportedEmail(email) ||
      !/^\d{6}$/.test(code) ||
      phase === 'submitting'
    ) {
      return;
    }

    setCodeError(false);
    setPhase('submitting');
    try {
      await verifyEmail({
        email: canonicalizeEmailForSubmit(email),
        code,
      });
      setPhase('success');
    } catch (error) {
      setCodeError(true);
      onApiError(error, t.auth.verify.invalidCode);
      setPhase('form');
    }
  };

  const handleResend = async () => {
    if (!isSupportedEmail(email) || resending || resendCooldown) return;
    setResending(true);
    try {
      await resendVerification(canonicalizeEmailForSubmit(email));
      setCode('');
      setCodeError(false);
      setResendCooldown(true);
      setTimeout(() => setResendCooldown(false), 60_000);
    } catch (error) {
      onApiError(error, t.auth.resendFailed);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {phase === 'legacy-verifying' && (
          <>
            <div className={styles.icon}>⏳</div>
            <div className={styles.title}>{t.auth.verify.verifying}</div>
          </>
        )}

        {phase === 'success' && (
          <>
            <div className={styles.icon}>✅</div>
            <div className={styles.title}>{t.auth.verify.successTitle}</div>
            <div className={styles.sub}>{t.auth.verify.redirecting}</div>
          </>
        )}

        {(phase === 'form' || phase === 'submitting') && (
          <>
            <div className={styles.icon}>✉️</div>
            <div className={styles.title}>{t.auth.verify.codeTitle}</div>
            <div className={styles.sub}>{t.auth.verify.codeSub}</div>
            <form onSubmit={submit} className={styles.resendForm} noValidate>
              <input
                className={styles.inp}
                type="email"
                value={email}
                readOnly
                disabled={phase === 'submitting'}
                aria-label={t.common.emailPlaceholder}
                tabIndex={-1}
              />
              <input
                className={`${styles.inp} ${styles.codeInput}`}
                type="text"
                placeholder="000000"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                  setCodeError(false);
                }}
                onBlur={() => setTouched(true)}
                disabled={phase === 'submitting'}
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                aria-label={t.auth.verify.codeLabel}
                required
                autoFocus
              />
              {((touched && code.length !== 6) || codeError) && (
                <div className={styles.fieldError}>
                  {codeError
                    ? t.auth.verify.invalidCode
                    : t.auth.verify.codeLength}
                </div>
              )}
              <GoldButton type="submit" disabled={phase === 'submitting'}>
                {phase === 'submitting'
                  ? t.auth.verify.submitting
                  : t.auth.verify.submit}
              </GoldButton>
              <button
                type="button"
                className={styles.textButton}
                onClick={handleResend}
                disabled={
                  resending || resendCooldown || !isSupportedEmail(email)
                }
              >
                {resending
                  ? t.common.sending
                  : resendCooldown
                    ? t.auth.emailSent
                    : t.auth.resendAgainShort}
              </button>
            </form>
            <Link to="/login" className={styles.backLink}>
              {t.common.backToLogin}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
